/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-07-24
 */

// Add third-party dependencies.
import { Typography, Box } from '@mui/material';

/**
 * Privacy Policy content component.
 * @example
 * return <PrivacyContent />
 * @returns {React.ReactElement} The Privacy Policy content component.
 */
const PrivacyContent: React.FC = () => (
  <Box>
    <Typography variant="body1" paragraph>
      This Privacy Policy (&quot;Policy&quot;) governs your use of the
      tools.dance platform and services provided by Little Shilling, Inc., an S
      Corporation organized under the laws of California (&quot;Company&quot;,
      &quot;we&quot;, &quot;us&quot;, or &quot;our&quot;). By accessing or using
      our services, you agree to be bound by this Privacy Policy.
    </Typography>

    <Typography variant="h4" component="h2" gutterBottom sx={{ mt: 4 }}>
      1. Information We Collect
    </Typography>

    <Typography variant="h5" component="h3" gutterBottom sx={{ mt: 3 }}>
      Personal Information
    </Typography>
    <Typography variant="body1" paragraph>
      We collect personal information that you voluntarily provide to us when
      you:
    </Typography>
    <Box component="ul" sx={{ pl: 3, mb: 2 }}>
      <Typography component="li" variant="body1">
        <strong>Account Registration:</strong> Name, email address, phone
        number, and password
      </Typography>
      <Typography component="li" variant="body1">
        <strong>Profile Information:</strong> Dance studio details, business
        information, and preferences
      </Typography>
      <Typography component="li" variant="body1">
        <strong>Payment Information:</strong> Billing address and payment method
        details (processed securely through third-party providers)
      </Typography>
      <Typography component="li" variant="body1">
        <strong>Communications:</strong> Messages, feedback, and support
        requests
      </Typography>
    </Box>

    <Typography variant="h5" component="h3" gutterBottom sx={{ mt: 3 }}>
      Automatically Collected Information
    </Typography>
    <Typography variant="body1" paragraph>
      When you use our platform, we automatically collect certain information:
    </Typography>
    <Box component="ul" sx={{ pl: 3, mb: 2 }}>
      <Typography component="li" variant="body1">
        <strong>Usage Data:</strong> Pages visited, features used, time spent on
        platform, and interaction patterns
      </Typography>
      <Typography component="li" variant="body1">
        <strong>Device Information:</strong> IP address, browser type, operating
        system, and device identifiers
      </Typography>
      <Typography component="li" variant="body1">
        <strong>Location Data:</strong> General geographic location based on IP
        address
      </Typography>
      <Typography component="li" variant="body1">
        <strong>Cookies and Tracking:</strong> Session data, preferences, and
        analytics information
      </Typography>
    </Box>

    <Typography variant="h4" component="h2" gutterBottom sx={{ mt: 4 }}>
      2. How We Use Your Information
    </Typography>

    <Typography variant="body1" paragraph>
      We use the collected information for the following purposes:
    </Typography>
    <Box component="ul" sx={{ pl: 3, mb: 2 }}>
      <Typography component="li" variant="body1">
        <strong>Service Provision:</strong> To provide, maintain, and improve
        our dance studio tools platform
      </Typography>
      <Typography component="li" variant="body1">
        <strong>Account Management:</strong> To create and manage your account,
        authenticate users, and provide customer support
      </Typography>
      <Typography component="li" variant="body1">
        <strong>Communication:</strong> To send service updates, security
        alerts, and respond to inquiries
      </Typography>
      <Typography component="li" variant="body1">
        <strong>Analytics:</strong> To analyze usage patterns and improve our
        services
      </Typography>
      <Typography component="li" variant="body1">
        <strong>Marketing:</strong> To send promotional materials (only with
        your consent)
      </Typography>
      <Typography component="li" variant="body1">
        <strong>Legal Compliance:</strong> To comply with legal obligations and
        protect our rights
      </Typography>
    </Box>

    <Typography variant="h4" component="h2" gutterBottom sx={{ mt: 4 }}>
      3. Information Sharing and Disclosure
    </Typography>

    <Typography variant="body1" paragraph>
      We do not sell, trade, or rent your personal information to third parties.
      We may share your information in the following circumstances:
    </Typography>

    <Typography variant="h5" component="h3" gutterBottom sx={{ mt: 3 }}>
      Service Providers
    </Typography>
    <Typography variant="body1" paragraph>
      We may share information with trusted third-party service providers who
      assist us in operating our platform:
    </Typography>
    <Box component="ul" sx={{ pl: 3, mb: 2 }}>
      <Typography component="li" variant="body1">
        <strong>Payment Processors:</strong> For secure payment processing and
        billing
      </Typography>
      <Typography component="li" variant="body1">
        <strong>Cloud Services:</strong> For data storage and hosting services
      </Typography>
      <Typography component="li" variant="body1">
        <strong>Analytics Providers:</strong> For usage analytics and
        performance monitoring
      </Typography>
      <Typography component="li" variant="body1">
        <strong>Communication Services:</strong> For email delivery and customer
        support
      </Typography>
    </Box>

    <Typography variant="h5" component="h3" gutterBottom sx={{ mt: 3 }}>
      Legal Requirements
    </Typography>
    <Typography variant="body1" paragraph>
      We may disclose your information when required by law or to:
    </Typography>
    <Box component="ul" sx={{ pl: 3, mb: 2 }}>
      <Typography component="li" variant="body1">
        Comply with legal processes, court orders, or government requests
      </Typography>
      <Typography component="li" variant="body1">
        Protect our rights, property, or safety, or that of our users
      </Typography>
      <Typography component="li" variant="body1">
        Investigate potential violations of our Terms of Service
      </Typography>
      <Typography component="li" variant="body1">
        Prevent fraud or security threats
      </Typography>
    </Box>

    <Typography variant="h4" component="h2" gutterBottom sx={{ mt: 4 }}>
      4. Data Storage and Security
    </Typography>

    <Typography variant="body1" paragraph>
      We implement appropriate technical and organizational security measures to
      protect your personal information:
    </Typography>
    <Box component="ul" sx={{ pl: 3, mb: 2 }}>
      <Typography component="li" variant="body1">
        <strong>Encryption:</strong> Data is encrypted in transit and at rest
        using industry-standard protocols
      </Typography>
      <Typography component="li" variant="body1">
        <strong>Access Controls:</strong> Strict access controls and
        authentication mechanisms
      </Typography>
      <Typography component="li" variant="body1">
        <strong>Regular Audits:</strong> Security assessments and vulnerability
        testing
      </Typography>
      <Typography component="li" variant="body1">
        <strong>Data Centers:</strong> Secure, certified data centers with
        physical and network security
      </Typography>
    </Box>

    <Typography variant="body1" paragraph>
      <strong>Data Retention:</strong> We retain your personal information for
      as long as necessary to provide our services and comply with legal
      obligations. Account data is typically retained for the duration of your
      account plus 7 years for business records, unless you request deletion.
    </Typography>

    <Typography variant="h4" component="h2" gutterBottom sx={{ mt: 4 }}>
      5. Your Privacy Rights
    </Typography>

    <Typography variant="body1" paragraph>
      Depending on your location, you may have the following rights regarding
      your personal information:
    </Typography>

    <Typography variant="h5" component="h3" gutterBottom sx={{ mt: 3 }}>
      General Rights
    </Typography>
    <Box component="ul" sx={{ pl: 3, mb: 2 }}>
      <Typography component="li" variant="body1">
        <strong>Access:</strong> Request access to your personal information
      </Typography>
      <Typography component="li" variant="body1">
        <strong>Correction:</strong> Request correction of inaccurate or
        incomplete information
      </Typography>
      <Typography component="li" variant="body1">
        <strong>Deletion:</strong> Request deletion of your personal information
      </Typography>
      <Typography component="li" variant="body1">
        <strong>Portability:</strong> Request a copy of your data in a portable
        format
      </Typography>
      <Typography component="li" variant="body1">
        <strong>Opt-out:</strong> Unsubscribe from marketing communications
      </Typography>
    </Box>

    <Typography variant="h5" component="h3" gutterBottom sx={{ mt: 3 }}>
      California Residents (CCPA)
    </Typography>
    <Typography variant="body1" paragraph>
      California residents have additional rights under the California Consumer
      Privacy Act (CCPA):
    </Typography>
    <Box component="ul" sx={{ pl: 3, mb: 2 }}>
      <Typography component="li" variant="body1">
        Right to know what personal information is collected and how it&apos;s
        used
      </Typography>
      <Typography component="li" variant="body1">
        Right to delete personal information
      </Typography>
      <Typography component="li" variant="body1">
        Right to opt-out of the sale of personal information (we do not sell
        personal information)
      </Typography>
      <Typography component="li" variant="body1">
        Right to non-discrimination for exercising privacy rights
      </Typography>
    </Box>

    <Typography variant="h4" component="h2" gutterBottom sx={{ mt: 4 }}>
      6. Cookies and Tracking Technologies
    </Typography>

    <Typography variant="body1" paragraph>
      We use cookies and similar tracking technologies to enhance your
      experience:
    </Typography>

    <Typography variant="h5" component="h3" gutterBottom sx={{ mt: 3 }}>
      Types of Cookies
    </Typography>
    <Box component="ul" sx={{ pl: 3, mb: 2 }}>
      <Typography component="li" variant="body1">
        <strong>Essential Cookies:</strong> Required for basic platform
        functionality and security
      </Typography>
      <Typography component="li" variant="body1">
        <strong>Performance Cookies:</strong> Help us analyze usage and improve
        our services
      </Typography>
      <Typography component="li" variant="body1">
        <strong>Functional Cookies:</strong> Remember your preferences and
        settings
      </Typography>
      <Typography component="li" variant="body1">
        <strong>Marketing Cookies:</strong> Used for targeted advertising (only
        with consent)
      </Typography>
    </Box>

    <Typography variant="body1" paragraph>
      You can control cookies through your browser settings, but disabling
      certain cookies may affect platform functionality.
    </Typography>

    <Typography variant="h4" component="h2" gutterBottom sx={{ mt: 4 }}>
      7. Third-Party Integrations
    </Typography>

    <Typography variant="body1" paragraph>
      Our platform may integrate with third-party services to enhance
      functionality:
    </Typography>
    <Box component="ul" sx={{ pl: 3, mb: 2 }}>
      <Typography component="li" variant="body1">
        <strong>Payment Processors:</strong> Stripe, PayPal for secure payment
        processing
      </Typography>
      <Typography component="li" variant="body1">
        <strong>Analytics:</strong> Google Analytics for usage insights
        (anonymized data)
      </Typography>
      <Typography component="li" variant="body1">
        <strong>Communication:</strong> Email service providers for
        transactional emails
      </Typography>
      <Typography component="li" variant="body1">
        <strong>Social Media:</strong> Optional social media integrations for
        sharing
      </Typography>
    </Box>

    <Typography variant="body1" paragraph>
      These third parties have their own privacy policies, and we encourage you
      to review them.
    </Typography>

    <Typography variant="h4" component="h2" gutterBottom sx={{ mt: 4 }}>
      8. International Data Transfers
    </Typography>

    <Typography variant="body1" paragraph>
      Your information may be transferred to and processed in countries other
      than your country of residence. We ensure appropriate safeguards are in
      place for international transfers, including:
    </Typography>
    <Box component="ul" sx={{ pl: 3, mb: 2 }}>
      <Typography component="li" variant="body1">
        Standard contractual clauses approved by regulatory authorities
      </Typography>
      <Typography component="li" variant="body1">
        Adequacy decisions by relevant data protection authorities
      </Typography>
      <Typography component="li" variant="body1">
        Other legally recognized transfer mechanisms
      </Typography>
    </Box>

    <Typography variant="h4" component="h2" gutterBottom sx={{ mt: 4 }}>
      9. Children&apos;s Privacy
    </Typography>

    <Typography variant="body1" paragraph>
      Our services are not intended for children under 13 years of age. We do
      not knowingly collect personal information from children under 13. If we
      become aware that we have collected personal information from a child
      under 13, we will take steps to delete such information promptly.
    </Typography>

    <Typography variant="h4" component="h2" gutterBottom sx={{ mt: 4 }}>
      10. Changes to This Privacy Policy
    </Typography>

    <Typography variant="body1" paragraph>
      We may update this Privacy Policy from time to time to reflect changes in
      our practices or legal requirements. We will notify you of material
      changes by:
    </Typography>
    <Box component="ul" sx={{ pl: 3, mb: 2 }}>
      <Typography component="li" variant="body1">
        Posting the updated policy on our website
      </Typography>
      <Typography component="li" variant="body1">
        Sending email notifications for significant changes
      </Typography>
      <Typography component="li" variant="body1">
        Providing in-app notifications when you next use our services
      </Typography>
    </Box>

    <Typography variant="body1" paragraph>
      Your continued use of our services after the effective date of the updated
      Privacy Policy constitutes acceptance of the changes.
    </Typography>

    <Typography variant="h4" component="h2" gutterBottom sx={{ mt: 4 }}>
      11. Contact Information
    </Typography>

    <Typography variant="body1" paragraph>
      If you have questions, concerns, or requests regarding this Privacy Policy
      or our data practices, please contact us:
    </Typography>

    <Box sx={{ pl: 2, mb: 2 }}>
      <Typography variant="body1" paragraph>
        <strong>Little Shilling, Inc.</strong>
        <br />
        Email: privacy@tools.dance
        <br />
        Address: [Company Address]
        <br />
        Phone: [Company Phone]
      </Typography>
    </Box>

    <Typography variant="body1" paragraph>
      <strong>Data Protection Officer:</strong> For EU-related inquiries,
      contact our Data Protection Officer at dpo@tools.dance
    </Typography>

    <Typography variant="body1" paragraph>
      We will respond to your inquiries within 30 days or as required by
      applicable law.
    </Typography>

    <Typography
      variant="body2"
      color="text.secondary"
      sx={{ mt: 4, fontStyle: 'italic' }}
    >
      This Privacy Policy is effective as of {new Date().toLocaleDateString()}{' '}
      and supersedes all previous versions.
    </Typography>
  </Box>
);

export default PrivacyContent;
